import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import chatIcon from "./chat_icon.png";
import {
  MessageCircle,
  X,
  Users,
  Zap,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  Activity,
  Plus,
  Minus,
  Lightbulb,
} from "lucide-react";
import { apiUrl } from "../../constants";

const FuningoAIBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [numPeople, setNumPeople] = useState("");
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activityFrequency, setActivityFrequency] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const navigate = useNavigate();
  // API Data States
  const [coinPackages, setCoinPackages] = useState([]);
  const [unlimitedPackages, setUnlimitedPackages] = useState([]);
  const [activities, setActivities] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [coinPackageRes, unlimitedPackageRes, activitiesRes] =
        await Promise.allSettled([
          axios.get(`${apiUrl}/package`),
          axios.get(`${apiUrl}/unlimitedPackage/packages`),
          axios.get(`${apiUrl}/activity/activityfetch`),
        ]);

      if (coinPackageRes.status === "fulfilled") {
        setCoinPackages(coinPackageRes.value.data.packages || []);
      } else {
        console.error("Error fetching coin packages:", coinPackageRes.reason);
        setCoinPackages([]);
      }

      if (unlimitedPackageRes.status === "fulfilled") {
        setUnlimitedPackages(unlimitedPackageRes.value.data.data || []);
      } else {
        console.error(
          "Error fetching unlimited packages:",
          unlimitedPackageRes.reason,
        );
        setUnlimitedPackages([]);
      }

      if (activitiesRes.status === "fulfilled") {
        setActivities(activitiesRes.value.data || []);
      } else {
        console.error("Error fetching activities:", activitiesRes.reason);
        setActivities([]);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const frequencyOptions = [
    { value: 1, label: "Once", icon: "1x" },
    { value: 2, label: "Twice", icon: "2x" },
    { value: 3, label: "3 times", icon: "3x" },
    { value: 5, label: "5 times", icon: "5x" },
    { value: 999, label: "Unlimited", icon: "∞" },
  ];

  const handleActivitySelection = (activityId) => {
    setSelectedActivities((prev) => {
      if (prev.includes(activityId)) {
        // Remove activity and its frequency
        const newFrequency = { ...activityFrequency };
        delete newFrequency[activityId];
        setActivityFrequency(newFrequency);
        return prev.filter((id) => id !== activityId);
      } else {
        // Add activity with default frequency of 1
        setActivityFrequency((prev) => ({
          ...prev,
          [activityId]: 1,
        }));
        return [...prev, activityId];
      }
    });
  };

  const handleFrequencyChange = (activityId, frequency) => {
    setActivityFrequency((prev) => ({
      ...prev,
      [activityId]: frequency,
    }));
  };

  const handleBookNow = async () => {
    if (!recommendation || !recommendation.packageDetails) return;

    try {
      setBookingLoading(true);

      // Prepare package data similar to Packages component navigation
      const packageData = {
        id: recommendation.packageDetails._id,
        type:
          recommendation.packageType === "Unlimited Package"
            ? "unlimited"
            : "regular",
        // Additional details that might be useful for booking
        packageName: recommendation.suggestedPackage,
        totalCost: recommendation.totalCost,
        selectedActivities: selectedActivities,
        activityFrequency: activityFrequency,
        numPeople: recommendation.people,
      };

      // Close the modal before navigating
      setIsOpen(false);

      // Navigate to booking page with package details (similar to Packages component)
      navigate("/book", {
        state: {
          selectedPackage: packageData,
          // Additional context from AI recommendation
          aiRecommendation: {
            selectedActivityDetails: recommendation.selectedActivityDetails,
            totalCoinsNeeded: recommendation.totalCoinsNeeded,
            alternative: recommendation.alternative,
            fromAI: true,
          },
        },
      });
    } catch (error) {
      console.error("Error navigating to booking:", error);
    } finally {
      setBookingLoading(false);
    }
  };

  const calculateRecommendation = () => {
    const people = parseInt(numPeople);

    // Calculate total coins needed for coin-based packages
    let totalCoinsNeeded = 0;
    const selectedActivityDetails = [];

    selectedActivities.forEach((activityId) => {
      const activity = activities.find((a) => a._id === activityId);
      const frequency = activityFrequency[activityId] || 1;

      if (activity) {
        const activityCoins = activity.coins_required * frequency * people;
        totalCoinsNeeded += activityCoins;
        selectedActivityDetails.push({
          name: activity.name,
          frequency,
          coinsPerPerson: activity.coins_required * frequency,
          totalCoins: activityCoins,
        });
      }
    });

    // Add 20% buffer
    const bufferedCoinsNeeded = Math.ceil(totalCoinsNeeded * 1.2);

    // Check if any activity has unlimited frequency
    const hasUnlimitedFrequency = selectedActivities.some(
      (activityId) => activityFrequency[activityId] === 999,
    );

    let suggestedPackage = null;
    let packageType = "Coin Package";
    let totalCost = 0;
    let alternative = "";
    let packageDetails = null;

    // If unlimited frequency is selected, check unlimited packages
    if (hasUnlimitedFrequency && unlimitedPackages.length > 0) {
      // Find the best unlimited package that includes selected activities
      const suitableUnlimitedPackage = unlimitedPackages.find((pkg) => {
        const packageActivityIds = pkg.activities.map((a) => a.activityId);
        return selectedActivities.every((selectedId) =>
          packageActivityIds.includes(selectedId),
        );
      });

      if (suitableUnlimitedPackage) {
        packageType = "Unlimited Package";
        suggestedPackage = suitableUnlimitedPackage.packageName;
        totalCost = suitableUnlimitedPackage.totalCost * people;
        packageDetails = suitableUnlimitedPackage;
        alternative = "Perfect for unlimited activity access!";
      }
    }

    // If no suitable unlimited package or no unlimited frequency, suggest coin package
    if (!suggestedPackage && coinPackages.length > 0) {
      // Sort coin packages by coins in ascending order
      const sortedCoinPackages = [...coinPackages].sort(
        (a, b) => a.coins - b.coins,
      );

      // Find the most suitable coin package
      const suitablePackage = sortedCoinPackages.find(
        (pkg) => pkg.coins >= bufferedCoinsNeeded,
      );

      if (suitablePackage) {
        suggestedPackage = suitablePackage.name;
        totalCost = suitablePackage.price;
        packageDetails = suitablePackage;

        // Check if unlimited package might be better value
        if (unlimitedPackages.length > 0) {
          const cheapestUnlimited = unlimitedPackages.reduce((min, pkg) =>
            pkg.totalCost < min.totalCost ? pkg : min,
          );

          if (cheapestUnlimited.totalCost * people < totalCost * 1.5) {
            alternative = `Consider "${cheapestUnlimited.packageName}" unlimited package for better value!`;
          }
        }
      } else {
        // If no single package is sufficient, suggest the largest one
        const largestPackage =
          sortedCoinPackages[sortedCoinPackages.length - 1];
        suggestedPackage = largestPackage.name;
        totalCost = largestPackage.price;
        packageDetails = largestPackage;
        alternative =
          "You may need multiple packages or consider unlimited options.";
      }
    }

    setRecommendation({
      packageType,
      suggestedPackage,
      totalCost,
      packageDetails,
      selectedActivityDetails,
      totalCoinsNeeded: bufferedCoinsNeeded,
      alternative,
      people,
      hasUnlimitedFrequency,
    });

    setStep(4);
  };

  const resetBot = () => {
    setStep(1);
    setNumPeople("");
    setSelectedActivities([]);
    setActivityFrequency({});
    setRecommendation(null);
  };

  const closeModal = () => {
    setIsOpen(false);
    // Reset all states when closing modal
    setTimeout(() => {
      resetBot();
    }, 300); // Delay to allow smooth closing animation
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Users className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                How many people are coming?
              </h3>
              <p className="text-gray-600 text-sm">
                Let me help you find the perfect package!
              </p>
            </div>

            <div className="space-y-3">
              <input
                type="number"
                value={numPeople}
                onChange={(e) => setNumPeople(e.target.value)}
                placeholder="Enter number of visitors"
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none text-center text-lg font-semibold transition-all duration-200"
                min="1"
                max="20"
              />

              {numPeople && (
                <button
                  onClick={() => setStep(2)}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-4 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight size={20} />
                </button>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Activity className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Which activities interest you?
              </h3>
              <p className="text-gray-600 text-sm">
                Select the activities you'd like to try (
                {selectedActivities.length} selected)
              </p>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {activities.map((activity) => (
                <div
                  key={activity._id}
                  onClick={() => handleActivitySelection(activity._id)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
                    selectedActivities.includes(activity._id)
                      ? "border-blue-400 bg-gradient-to-r from-blue-50 to-purple-50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedActivities.includes(activity._id)
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {selectedActivities.includes(activity._id) && (
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800">
                          {activity.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {activity.coins_required} coins per person
                        </div>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedActivities.includes(activity._id)
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {activity.coins_required} coins
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 p-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} /> Back
              </button>

              {selectedActivities.length > 0 && (
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Next <ArrowRight size={20} />
                </button>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Zap className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                How many times per activity?
              </h3>
              <p className="text-gray-600 text-sm">
                Set frequency for each selected activity
              </p>
            </div>

            <div className="space-y-4 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {selectedActivities.map((activityId) => {
                const activity = activities.find((a) => a._id === activityId);
                if (!activity) return null;

                const currentFrequency = activityFrequency[activityId] || 1;

                return (
                  <div
                    key={activityId}
                    className="p-4 border-2 border-gray-200 rounded-xl bg-gradient-to-r from-gray-50 to-white"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold text-gray-800">
                          {activity.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {activity.coins_required} coins per person per session
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-purple-600">
                          {currentFrequency === 999
                            ? "∞"
                            : `${currentFrequency}x`}
                        </div>
                        <div className="text-sm text-gray-500">
                          {currentFrequency === 999
                            ? "Unlimited"
                            : `${
                                activity.coins_required * currentFrequency
                              } coins/person`}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-5 gap-2">
                      {frequencyOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() =>
                            handleFrequencyChange(activityId, option.value)
                          }
                          className={`p-3 rounded-lg text-center transition-all duration-200 ${
                            currentFrequency === option.value
                              ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg transform scale-105"
                              : "bg-white border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                          }`}
                        >
                          <div className="font-bold text-lg">{option.icon}</div>
                          <div className="text-xs mt-1">{option.label}</div>
                        </button>
                      ))}
                    </div>

                    <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-700">
                        <strong>Total for {numPeople} people:</strong>{" "}
                        {currentFrequency === 999
                          ? "Unlimited access"
                          : `${
                              activity.coins_required *
                              currentFrequency *
                              parseInt(numPeople)
                            } coins`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 p-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} /> Back
              </button>

              <button
                onClick={calculateRecommendation}
                className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
              >
                Get Recommendation <Sparkles size={20} />
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Sparkles className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Perfect Match Found!
              </h3>
              <p className="text-gray-600 text-sm">
                Here's what I recommend for your group
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-2xl border-2 border-green-200 shadow-md">
              <div className="text-center mb-4">
                <h4 className="text-2xl font-bold text-gray-800">
                  {recommendation.suggestedPackage}
                </h4>
                <div className="text-3xl font-bold text-green-600 mt-2">
                  ₹{recommendation.totalCost.toLocaleString()}
                  {recommendation.packageType === "Unlimited Package" && (
                    <span className="text-sm text-gray-600">
                      {" "}
                      for {recommendation.people} people
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Package Type:</span>
                  <span className="font-semibold">
                    {recommendation.packageType}
                  </span>
                </div>

                {recommendation.packageType === "Coin Package" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coins Included:</span>
                      <span className="font-semibold">
                        {recommendation.packageDetails?.coins?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coins Needed:</span>
                      <span className="font-semibold">
                        {recommendation.totalCoinsNeeded.toLocaleString()}
                      </span>
                    </div>
                  </>
                )}

                {/* {recommendation.packageType === "Unlimited Package" && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Valid Days:</span>
                    <span className="font-semibold">
                      {recommendation.packageDetails?.selectedDays?.length || 0}{" "}
                      days
                    </span>
                  </div>
                )} */}
              </div>

              {/* Activity Breakdown */}
              <div className="mt-4 p-3 bg-white bg-opacity-50 rounded-lg">
                <h5 className="font-semibold text-gray-800 mb-2">
                  Selected Activities:
                </h5>
                <div className="space-y-1">
                  {recommendation.selectedActivityDetails.map(
                    (activity, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span>
                          {activity.name} ×{" "}
                          {activity.frequency === 999
                            ? "∞"
                            : activity.frequency}
                        </span>
                        <span>
                          {activity.frequency === 999
                            ? "Unlimited"
                            : `${activity.totalCoins} coins`}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {recommendation.alternative && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm font-medium">
                    💡 {recommendation.alternative}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleBookNow}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-4 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Buy This Package
              </button>

              <button
                onClick={resetBot}
                className="w-full p-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
              >
                Try Another Combination
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed bottom-6 left-6 z-50">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const ChatModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm">
      <div
        className="bg-white rounded-3xl w-full max-w-lg h-[85vh] max-h-[800px] overflow-hidden transform scale-100 transition-all duration-300 flex flex-col"
        style={{
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 50px -12px rgba(139, 92, 246, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.05)",
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 p-5 text-white relative overflow-hidden flex-shrink-0">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full translate-y-12 -translate-x-12"></div>

          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={chatIcon}
                  alt="Chat Bot"
                  className="w-10 h-10 object-contain"
                />
                {/* Online status */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm">
                  <div className="w-full h-full bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>
              <div>
                <h2 className="font-bold text-lg">Funingo AI Assistant</h2>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-sm opacity-90">Online now</p>
                </div>
              </div>
            </div>
            <button
              onClick={closeModal}
              className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all duration-200 backdrop-blur-sm border border-white border-opacity-20"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress indicator */}
          <div className="mt-4 flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  i <= step ? "bg-white" : "bg-white bg-opacity-30"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">{renderStep()}</div>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-6 left-6 z-50">
      {/* Modern Chatbot Icon */}
      {!isOpen && (
        <div className="relative">
          {" "}
          <div
            className="absolute bottom-20 left-0 bg-white rounded-2xl shadow-2xl p-4 mb-2 w-80 transform -translate-x-4 opacity-0 hover-tooltip transition-all duration-300 border border-gray-100 pointer-events-none" // Added custom class and pointer-events-none
            style={{
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-gray-800">
                Funingo Assistant
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Hi! Need help choosing the perfect package? 🎢
            </p>
            <p className="text-xs text-gray-500">
              I'll help you find the best package based on your group size and
              preferred activities!
            </p>
            <div className="absolute bottom-0 left-4 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white transform translate-y-full"></div>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="w-16 h-16 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 rounded-2xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center group relative overflow-hidden" // Added 'group' class here
            style={{
              boxShadow:
                "0 10px 25px rgba(139, 92, 246, 0.3), 0 20px 40px -10px rgba(139, 92, 246, 0.2)",
            }}
            onMouseEnter={() => {
              // Show tooltip when hovering over button
              const tooltip = document.querySelector(".hover-tooltip");
              if (tooltip) {
                tooltip.style.opacity = "1";
                tooltip.style.transform = "translateX(0)";
              }
            }}
            onMouseLeave={() => {
              // Hide tooltip when leaving button
              const tooltip = document.querySelector(".hover-tooltip");
              if (tooltip) {
                tooltip.style.opacity = "0";
                tooltip.style.transform = "translateX(-1rem)";
              }
            }}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-8 h-8 bg-white opacity-10 rounded-full -translate-y-4 translate-x-4"></div>
            <div className="absolute bottom-0 left-0 w-6 h-6 bg-white opacity-10 rounded-full translate-y-3 -translate-x-3"></div>

            {/* Enhanced Chatbot Icon */}
            <img
              src={chatIcon}
              alt="Chat Bot"
              className="w-24 h-24 object-contain relative z-10"
            />

            {/* Notification badge */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg animate-pulse">
              !
            </div>
          </button>
          {/* Floating animation particles */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div
              className="w-20 h-20 border-2 border-purple-300 border-opacity-30 rounded-full animate-spin"
              style={{ animationDuration: "3s" }}
            ></div>
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-indigo-300 border-opacity-20 rounded-full animate-spin"
              style={{ animationDuration: "2s", animationDirection: "reverse" }}
            ></div>
          </div>
        </div>
      )}

      {/* Modal */}
      {isOpen && createPortal(<ChatModal />, document.body)}
    </div>
  );
};

export default FuningoAIBot;
